import { useMemo, useState } from 'react';
import type { FormField } from '@features/ksp-actions/ActionForm';
import type {
  KspRdConfirmation,
  KspRdCondition,
  KspRdDocument,
  KspRdImport,
  KspRdPlan,
  KspRdRequirement,
  KspRdRevision,
} from '@entities/ksp/types';
import {
  useGetKspRdDatesQuery,
  useGetKspRdShiftQuery,
  useUploadKspRdIssueMutation,
} from '@shared/api/endpoints/ksp';
import { Notice, Stack } from '@widgets/ksp-workspace/parts';
import { parsePages } from '../rdPages';
import { CONDITION_LABEL, revisionName, ROW_STATUS, ru, type RdDatesTabProps } from './types';
import { RdSummaryCard } from './components/RdSummaryCard';
import { RdShiftCard } from './components/RdShiftCard';
import { RdPlanImportsCard } from './components/RdPlanImportsCard';
import { RdRequirementsCard } from './components/RdRequirementsCard';
import { RdReadinessCard } from './components/RdReadinessCard';
import { RdDocumentsCard } from './components/RdDocumentsCard';

export function RdDatesTab({
  projectId,
  version,
  canEdit,
  execute,
  open,
  onError,
  workOptions,
}: RdDatesTabProps) {
  const { data, isFetching, error } = useGetKspRdDatesQuery(projectId, { pollingInterval: 15000 });
  const shift = useGetKspRdShiftQuery({ projectId }, { pollingInterval: 30000 });
  const [upload, uploading] = useUploadKspRdIssueMutation();
  const [file, setFile] = useState<File | null>(null);
  const [filter, setFilter] = useState('');
  const [shown, setShown] = useState(50);
  const [openImport, setOpenImport] = useState('');
  const base = `projects/${projectId}/rd-dates`;

  const confirmations = useMemo(() => {
    const byRevision = new Map<string, KspRdConfirmation[]>();
    for (const c of data?.confirmations ?? []) {
      if (c.status !== 'active') continue;
      byRevision.set(c.original_revision_id, [...(byRevision.get(c.original_revision_id) ?? []), c]);
    }
    return byRevision;
  }, [data?.confirmations]);
  const plans = useMemo(
    () => (data?.plan ?? []).filter((p) => p.status === 'active'),
    [data?.plan],
  );
  const planFor = (doc: KspRdDocument) =>
    plans.find((p) => p.document_id === doc.id) ?? undefined;
  const documentOptions = (data?.documents ?? [])
    .filter((d) => !d.deleted)
    .map((d) => ({ value: d.id, label: `${d.code} — ${d.title}` }));
  const activityLabel = new Map(workOptions.map((o) => [o.value, o.label]));
  const documents = (data?.documents ?? []).filter((d) =>
    `${d.code} ${d.title}`.toLowerCase().includes(filter.trim().toLowerCase()),
  );
  const proposed = (data?.requirements ?? []).filter((r) => r.status === 'proposed');

  const confirmForm = (doc: KspRdDocument, revision: KspRdRevision, condition: KspRdCondition) => {
    const candidate =
      condition === 'receipt' ? revision.candidates.receipt : revision.candidates.permit;
    const candidateSource = condition === 'receipt' ? 'pto_uploaded_at' : 'pto_afc_stamp';
    const sources = [
      ...(candidate
        ? [
            {
              value: candidateSource,
              label:
                condition === 'receipt'
                  ? `Кандидат ПТО: ревизия загружена в ОСНОВУ ${ru(candidate)} (это не дата получения — проверьте)`
                  : `Кандидат ПТО: штамп «В производство работ» ${ru(candidate)}${revision.candidates.permit_signer ? `, ${revision.candidates.permit_signer}` : ''}`,
            },
          ]
        : []),
      { value: 'engineer', label: 'Своя дата (нужна причина)' },
    ];
    open(
      `${CONDITION_LABEL[condition]}: ${doc.code}, ${revisionName(revision)}`,
      [
        { key: 'date', label: 'Дата', type: 'date', required: true, value: candidate ?? '' },
        {
          key: 'source',
          label: 'Основание даты',
          type: 'select',
          required: true,
          value: sources[0].value,
          options: sources,
          help: 'Кандидат ПТО подтверждается как есть. Другая дата — только как дата инженера с причиной.',
        },
        {
          key: 'pages',
          label: 'Листы, к которым относится (через запятую, диапазоны через дефис; пусто — комплект целиком)',
          required: false,
          value:
            condition === 'permit' && revision.candidates.permit_pages?.length
              ? revision.candidates.permit_pages.join(', ')
              : '',
        },
        { key: 'reason', label: 'Причина / комментарий', type: 'textarea', required: false },
      ],
      (values) =>
        execute(`${base}/confirmations`, {
          revision_id: revision.id,
          condition,
          date: values.date,
          source: values.source,
          // нераспознанные листы — ошибка в форме, а не «комплект целиком»
          pages: parsePages(values.pages),
          reason: values.reason,
        }),
      'Подтвердить',
    );
  };
  const reasonForm = (title: string, path: string, body: Record<string, unknown>) =>
    open(
      title,
      [{ key: 'reason', label: 'Причина', type: 'textarea', required: true }],
      (values) => execute(path, { ...body, reason: values.reason }),
    );
  const planForm = () =>
    open(
      'Плановая дата выдачи вручную',
      [
        {
          key: 'document_id',
          label: 'Комплект из реестра РД ПТО',
          type: 'select',
          required: false,
          options: [{ value: '', label: '— комплекта нет в реестре, указать шифр —' }, ...documentOptions],
        },
        { key: 'set_code', label: 'Шифр ожидаемого комплекта (если его нет в реестре)', required: false },
        { key: 'planned_on', label: 'Плановая дата выдачи', type: 'date', required: true },
        { key: 'reason', label: 'Причина ручной даты', type: 'textarea', required: true },
      ],
      (values) =>
        execute(`${base}/plan`, {
          document_id: values.document_id || undefined,
          set_code: values.set_code || undefined,
          planned_on: values.planned_on,
          reason: values.reason,
        }),
    );
  const requirementForm = (existing?: KspRdRequirement) =>
    open(
      existing ? `Условия готовности: ${existing.set_code}` : 'Связать работу с комплектом РД',
      [
        ...(existing
          ? []
          : ([
              {
                key: 'activity_id',
                label: 'Работа графика',
                type: 'select',
                required: true,
                options: workOptions,
              },
              {
                key: 'document_id',
                label: 'Комплект из реестра РД ПТО',
                type: 'select',
                required: false,
                options: [{ value: '', label: '— ожидаемый комплект, указать шифр —' }, ...documentOptions],
              },
              { key: 'set_code', label: 'Шифр ожидаемого комплекта', required: false },
            ] as FormField[])),
        {
          key: 'needs_receipt',
          label: 'Нужно получение РД',
          type: 'select',
          required: true,
          value: existing ? String(existing.needs_receipt) : 'true',
          options: [
            { value: 'true', label: 'Да' },
            { value: 'false', label: 'Нет' },
          ],
        },
        {
          key: 'needs_permit',
          label: 'Нужно разрешение к производству (штамп «В производство работ»)',
          type: 'select',
          required: true,
          value: existing ? String(existing.needs_permit) : 'true',
          options: [
            { value: 'true', label: 'Да' },
            { value: 'false', label: 'Нет' },
          ],
        },
      ],
      (values) =>
        execute(`${base}/requirements`, {
          activity_id: existing?.activity_id ?? values.activity_id,
          document_id: existing?.document_id ?? (values.document_id || undefined),
          set_code: existing?.set_code ?? (values.set_code || undefined),
          needs_receipt: values.needs_receipt === 'true',
          needs_permit: values.needs_permit === 'true',
        }),
      existing ? 'Сохранить и подтвердить' : 'Связать',
    );
  const linkForm = (plan: KspRdPlan) =>
    open(
      `Связать ожидаемый комплект ${plan.set_code} с документом ПТО`,
      [
        {
          key: 'document_id',
          label: 'Документ реестра РД ПТО',
          type: 'select',
          required: true,
          options: documentOptions,
        },
        {
          key: 'reason',
          label: 'Причина (обязательна, если шифр документа отличается)',
          type: 'textarea',
          required: false,
        },
      ],
      (values) =>
        execute(`${base}/link`, {
          set_code: plan.set_code,
          document_id: values.document_id,
          reason: values.reason,
        }),
    );
  const sendFile = async () => {
    if (!file || version === undefined) return;
    try {
      const result = (await upload({
        projectId,
        file,
        key: crypto.randomUUID(),
        expectedVersion: version,
      }).unwrap()) as { id?: string };
      setFile(null);
      if (result.id) setOpenImport(result.id);
    } catch (e) {
      onError(e);
    }
  };

  if (error) return <Notice $error>Не удалось загрузить даты РД. Обновите страницу.</Notice>;
  if (!data) return <Notice>Загружаются даты выдачи РД…</Notice>;
  const run = data.latest_run;
  const counts = (imp: KspRdImport) =>
    Object.entries(
      imp.rows.reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1 }), {}),
    )
      .map(([status, n]) => `${ROW_STATUS[status] ?? status}: ${n}`)
      .join('; ');

  return (
    <Stack>
      <RdSummaryCard timezone={data.timezone} run={run} isFetching={isFetching} />
      <RdShiftCard shift={shift} />
      <RdPlanImportsCard
        canEdit={canEdit}
        file={file}
        setFile={setFile}
        uploading={uploading.isLoading}
        sendFile={sendFile}
        planForm={planForm}
        imports={data.imports}
        openImport={openImport}
        setOpenImport={setOpenImport}
        plans={plans}
        counts={counts}
        onAcceptImport={(impId) => execute(`${base}/plan/accept`, { import_id: impId }).catch(() => undefined)}
        onLink={linkForm}
        onRemovePlan={(p) => reasonForm(`Снять плановую дату ${p.set_code}`, `${base}/plan/remove`, { id: p.id })}
      />
      <RdRequirementsCard
        canEdit={canEdit}
        requirements={data.requirements}
        proposed={proposed}
        activityLabel={activityLabel}
        onPrefill={() => execute(`${base}/requirements/prefill`, {}).catch(() => undefined)}
        onConfirmProposed={() =>
          execute(`${base}/requirements/confirm`, { ids: proposed.map((r) => r.id) }).catch(() => undefined)
        }
        onAddRequirement={() => requirementForm()}
        onEditRequirement={(r) => requirementForm(r)}
        onRemoveRequirement={(r) => reasonForm(`Убрать связь ${r.set_code}`, `${base}/requirements/remove`, { id: r.id })}
      />
      <RdReadinessCard
        readiness={data.readiness}
        activityLabel={activityLabel}
        timezone={data.timezone}
      />
      <RdDocumentsCard
        canEdit={canEdit}
        filter={filter}
        setFilter={setFilter}
        documents={documents}
        shown={shown}
        setShown={setShown}
        confirmations={confirmations}
        planFor={planFor}
        onConfirm={confirmForm}
        onRevoke={(c) => reasonForm('Отменить подтверждение даты', `${base}/confirmations/revoke`, { id: c.id })}
        totalDocuments={data.documents.length}
      />
    </Stack>
  );
}
